import * as Sentry from '@sentry/react';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { playAlertSound } from '../lib/alert-sound-player.js';
import {
  rememberAnnouncedDeviceAlerts,
  selectDeviceAlertIdsNewSinceSnapshot,
  selectNewDeviceAlertIds,
  snapshotCachedDeviceAlertIds,
} from '../lib/device-alerts.js';
import {
  isDeviceSubscribed,
  resubscribeAll,
  type SubscribeAck,
  subscribeDevices,
  unsubscribeDevices,
} from '../sockets/deviceSubscriptionManager';
import { acquireFrontendSocket, releaseFrontendSocket } from '../sockets/frontendSocket';
import { scheduleInvalidation } from '../sockets/invalidationScheduler.js';
import { useActiveSuppressionsStore } from '../store/activeSuppressions.js';
import {
  type ActiveSuppressionRecord,
  type BroadbandSuppression,
  type Detection,
  type DetectionGroup,
  DetectionStatus,
  type Device,
  type FirmwareUpdateStatusEvent,
  type Suppression,
} from '../types';
import { isTerminalJumperSessionStatus, type JumperSession } from '../types/jumper-session';
import { parseDetectionGroup } from './useDetectionGroups.js';

export type FrontendDetectionEvent = {
  deviceId: string;
  detection: Detection;
};
export type FrontendSuppressionEvent = {
  deviceId: string;
  suppression: Suppression;
};
export type FrontendBroadbandSuppressionEvent = {
  deviceId: string;
  broadbandSuppression: BroadbandSuppression;
};
export type FrontendDetectionGroupStatusEvent = {
  deviceId: string;
  detectionGroupId: string;
  detectionGroupCreatedAt: string;
  status: DetectionStatus;
};
export type FrontendDetectionGroupCreatedEvent = {
  deviceId: string;
  detectionGroup: DetectionGroup;
};
export type FrontendDetectionGroupUpdatedEvent = {
  deviceId: string;
  detectionGroup: DetectionGroup;
};

export type FrontendDeviceUpdatedEvent = {
  deviceId: string;
  updates: Partial<Device> & { activeSuppressions?: ActiveSuppressionRecord[] };
};

export type FrontendDeviceConnectedEvent = {
  deviceId: string;
  connectedAt: string;
};

export type FrontendActiveDetectionGroupsCountUpdatedEvent = {
  deviceId: string;
  activeDetectionGroupsCount: number;
};

export type FrontendJumperSessionUpdatedEvent = {
  deviceId: string;
  session: JumperSession;
};

type DeviceStatusSocketHandlers = {
  onNewDetection?: (event: FrontendDetectionEvent) => void;
  onNewDetectionGroup?: (event: FrontendDetectionGroupCreatedEvent) => void;
  onNewBroadbandSuppression?: (event: FrontendBroadbandSuppressionEvent) => void;
  onNewSuppression?: (event: FrontendSuppressionEvent) => void;
  onDetectionGroupStatusChange?: (event: FrontendDetectionGroupStatusEvent) => void;
  onFirmwareUpdateStatus?: (event: FirmwareUpdateStatusEvent) => void;
};

// An alert raised during a socket gap reaches the cache only through this refetch, never as a frame.
// Every mounted hook runs this on the same reconnect; the player's throttle absorbs the duplicates.
const refetchDevicesAndAnnounceNewAlerts = async (queryClient: QueryClient): Promise<void> => {
  const cachedAlertIds = snapshotCachedDeviceAlertIds(queryClient);
  try {
    await queryClient.invalidateQueries({ queryKey: ['devices'] });
  } catch {
    // The cache was not updated, so there is nothing new to announce.
    return;
  }
  const newAlertIds = selectDeviceAlertIdsNewSinceSnapshot(queryClient, cachedAlertIds);
  if (newAlertIds.length > 0) {
    rememberAnnouncedDeviceAlerts(newAlertIds);
    playAlertSound(newAlertIds);
  }
};

export const useDeviceStatusSocket = (
  handlers: DeviceStatusSocketHandlers = {},
  deviceIds?: string[],
) => {
  const queryClient = useQueryClient();
  // TODO: pass onNewSuppression in Suppressions page
  const {
    onNewDetection,
    onNewDetectionGroup,
    onNewBroadbandSuppression,
    onNewSuppression,
    onDetectionGroupStatusChange,
    onFirmwareUpdateStatus,
  } = handlers;

  useEffect(() => {
    const socket = acquireFrontendSocket();

    const handleDisconnect = () => {
      // refCounts intentionally preserved so resubscribeAll can restore them on reconnect
    };

    const handleReconnect = () => {
      // The subscribe snapshot is the only repopulation path, so drop anything missed while away.
      useActiveSuppressionsStore.getState().clearAll();
      resubscribeAll();
      // Invalidate all device-related queries to refresh potentially stale data
      // that was missed during the WebSocket disconnection period
      void queryClient.invalidateQueries({ queryKey: ['detection-groups'] });
      void queryClient.invalidateQueries({ queryKey: ['all-suppressions'] });
      void queryClient.invalidateQueries({ queryKey: ['broadband-suppressions'] });
      void queryClient.invalidateQueries({ queryKey: ['suppressions'] });
      void queryClient.invalidateQueries({ queryKey: ['group-detections'] });
      void refetchDevicesAndAnnounceNewAlerts(queryClient);
    };

    const handleNewDetection = ({ deviceId, detection }: FrontendDetectionEvent) => {
      onNewDetection?.({ deviceId, detection });
    };

    const handleNewDetectionGroup = (data: FrontendDetectionGroupCreatedEvent) => {
      onNewDetectionGroup?.(data);
    };

    const handleDetectionGroupUpdated = (data: FrontendDetectionGroupUpdatedEvent) => {
      // Third ingress into the detection-groups cache; guarded like the other two.
      const incoming = parseDetectionGroup(data.detectionGroup);
      const incomingParentId =
        (incoming as { parentDetectionGroupId?: string | null }).parentDetectionGroupId ?? null;

      const matchingQueries = queryClient.getQueriesData<{
        groups: DetectionGroup[];
        total: number;
      }>({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key) || key[0] !== 'detection-groups') return false;
          const params = key[1] as { deviceId?: string } | undefined;
          return !params?.deviceId || params.deviceId === data.deviceId;
        },
      });

      for (const [queryKey, oldData] of matchingQueries) {
        if (!oldData) continue;

        const params = ((Array.isArray(queryKey) ? queryKey[1] : undefined) ?? {}) as {
          parentDetectionGroupId?: string;
          includeParent?: boolean;
        };
        const queryParentFilter =
          'parentDetectionGroupId' in params ? (params.parentDetectionGroupId ?? null) : undefined;
        const existingIndex = oldData.groups.findIndex((group) => group.id === incoming.id);
        const isPresent = existingIndex >= 0;

        // Query has no parent filter: replace in place if present; otherwise schedule an
        // invalidation (debounced) so a reused group off the current page surfaces on refetch.
        if (queryParentFilter === undefined) {
          if (!isPresent) {
            scheduleInvalidation(queryClient, data.deviceId, ['detection-groups']);
            continue;
          }
          const groups = oldData.groups.slice();
          groups[existingIndex] = { ...groups[existingIndex], ...incoming };
          queryClient.setQueryData(queryKey, { ...oldData, groups });
          continue;
        }

        const matchesFilter = queryParentFilter === incomingParentId;

        if (matchesFilter) {
          if (isPresent) {
            const groups = oldData.groups.slice();
            groups[existingIndex] = { ...groups[existingIndex], ...incoming };
            queryClient.setQueryData(queryKey, { ...oldData, groups });
          } else {
            queryClient.setQueryData(queryKey, {
              ...oldData,
              groups: [incoming as DetectionGroup, ...oldData.groups],
              total: oldData.total + 1,
            });
          }
          continue;
        }

        // Pinned-parent guard: only applies to include-parent queries, where the modal
        // prepends the session's initial group whose own id equals this query's
        // parentDetectionGroupId. Its parentDetectionGroupId is null (or its grandparent),
        // so matchesFilter is false — but for an include-parent query it must never be
        // evicted. Children-only queries (includeParent !== true) fall through so a stray
        // anchor is evicted-if-present / ignored-if-absent.
        if (params.includeParent === true && incoming.id === queryParentFilter) {
          if (isPresent) {
            // (a) present + is-parent → upsert in place, never evict.
            const groups = oldData.groups.slice();
            groups[existingIndex] = { ...groups[existingIndex], ...incoming };
            queryClient.setQueryData(queryKey, { ...oldData, groups });
          } else {
            // (b) absent + is-parent → prepend without incrementing total (children-only).
            queryClient.setQueryData(queryKey, {
              ...oldData,
              groups: [incoming as DetectionGroup, ...oldData.groups],
            });
          }
          continue;
        }

        if (isPresent) {
          const groups = oldData.groups.filter((group) => group.id !== incoming.id);
          queryClient.setQueryData(queryKey, {
            ...oldData,
            groups,
            total: Math.max(0, oldData.total - 1),
          });
        }
      }
    };

    const handleNewSuppression = (data: FrontendSuppressionEvent) => {
      onNewSuppression?.(data);
    };

    const handleNewBroadbandSuppression = (data: FrontendBroadbandSuppressionEvent) => {
      onNewBroadbandSuppression?.(data);
    };

    const handleDetectionGroupStatusChanged = (data: FrontendDetectionGroupStatusEvent) => {
      queryClient.setQueriesData<{ groups: DetectionGroup[]; total: number }>(
        {
          predicate: (query) => {
            const key = query.queryKey;
            if (!Array.isArray(key) || key[0] !== 'detection-groups') return false;

            const params = key[1] as { deviceId?: string } | undefined;
            return !params?.deviceId || params.deviceId === data.deviceId;
          },
        },
        (oldData) => {
          if (!oldData) return oldData;

          let changed = false;
          const groups = oldData.groups.map((group) => {
            if (
              group.id !== data.detectionGroupId ||
              group.createdAt !== data.detectionGroupCreatedAt
            ) {
              return group;
            }

            changed = true;
            return {
              ...group,
              status: data.status,
              ...(data.status !== DetectionStatus.SUPPRESSION_IN_PROGRESS && {
                suppressionDuration: null,
                suppressionStartedAt: null,
              }),
            };
          });

          return changed ? { ...oldData, groups } : oldData;
        },
      );
      scheduleInvalidation(queryClient, data.deviceId, [
        'detection-groups',
        'suppressions',
        'group-detections',
      ]);
      onDetectionGroupStatusChange?.(data);
    };

    const handleDeviceUpdated = (data: FrontendDeviceUpdatedEvent) => {
      const { activeSuppressions, ...deviceUpdates } = data.updates;

      // A subscribe snapshot can land after the last subscriber left, and the server emits no empty
      // set, so a re-subscription could never repair the entry this would resurrect.
      if (activeSuppressions && isDeviceSubscribed(data.deviceId)) {
        useActiveSuppressionsStore
          .getState()
          .setDeviceActiveSuppressions(data.deviceId, activeSuppressions);
      }

      // A snapshot frame carries nothing else; rewriting every cached device would re-render the grid.
      if (Object.keys(deviceUpdates).length === 0) {
        return;
      }

      // Every mount runs this handler; reading before its own merge leaves an id new only for the first.
      const newAlertIds =
        deviceUpdates.alerts === undefined
          ? []
          : selectNewDeviceAlertIds(queryClient, data.deviceId, deviceUpdates.alerts);

      queryClient.setQueriesData<{ devices: Device[]; total: number }>(
        { predicate: (query) => query.queryKey[0] === 'devices' },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            devices: oldData.devices.map((device) =>
              device.id === data.deviceId
                ? {
                    ...device,
                    ...deviceUpdates,
                    settings: deviceUpdates.settings
                      ? { ...device.settings, ...deviceUpdates.settings }
                      : device.settings,
                  }
                : device,
            ),
          };
        },
      );

      if (newAlertIds.length > 0) {
        rememberAnnouncedDeviceAlerts(newAlertIds);
        playAlertSound(newAlertIds);
      }
    };

    // Status and lastSeenAt arrive separately as device_updated. A reconnect can follow an offline
    // gap or a device restart, so refetch the device-scoped lists this client may hold stale.
    const handleDeviceConnected = ({ deviceId }: FrontendDeviceConnectedEvent) => {
      scheduleInvalidation(queryClient, deviceId, [
        'detection-groups',
        'suppressions',
        'broadband-suppressions',
      ]);
    };

    const handleActiveDetectionGroupsCountUpdated = (
      data: FrontendActiveDetectionGroupsCountUpdatedEvent,
    ) => {
      console.log('Device counter updated:', data.deviceId, data.activeDetectionGroupsCount);

      queryClient.setQueriesData<{ devices: Device[]; total: number }>(
        { predicate: (query) => query.queryKey[0] === 'devices' },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            devices: oldData.devices.map((device) =>
              device.id === data.deviceId
                ? { ...device, activeDetectionGroupsCount: data.activeDetectionGroupsCount }
                : device,
            ),
          };
        },
      );
    };

    const handleFirmwareUpdateStatus = (data: FirmwareUpdateStatusEvent) => {
      onFirmwareUpdateStatus?.(data);
    };

    const handleJumperSessionUpdated = ({
      deviceId,
      session,
    }: FrontendJumperSessionUpdatedEvent) => {
      if (session.deviceId !== deviceId) {
        Sentry.captureMessage('jumper_session_updated deviceId mismatch', {
          level: 'warning',
          extra: { envelopeDeviceId: deviceId, sessionDeviceId: session.deviceId },
        });
        return;
      }
      const cacheKey = ['jumper-sessions', 'active', { deviceId }];
      if (isTerminalJumperSessionStatus(session.status)) {
        queryClient.setQueryData(cacheKey, null);
        return;
      }
      queryClient.setQueryData(cacheKey, session);
    };

    socket.on('disconnect', handleDisconnect);
    socket.on('connect', handleReconnect);
    socket.on('new_detection', handleNewDetection);
    socket.on('new_detection_group', handleNewDetectionGroup);
    socket.on('detection_group_updated', handleDetectionGroupUpdated);
    socket.on('new_broadband_suppression', handleNewBroadbandSuppression);
    socket.on('new_suppression', handleNewSuppression);
    socket.on('detection_group_status_changed', handleDetectionGroupStatusChanged);
    socket.on('device_updated', handleDeviceUpdated);
    socket.on('device_connected', handleDeviceConnected);
    socket.on(
      'device_active_detection_groups_counter_updated',
      handleActiveDetectionGroupsCountUpdated,
    );
    socket.on('firmware_update_status', handleFirmwareUpdateStatus);
    socket.on('jumper_session_updated', handleJumperSessionUpdated);

    return () => {
      socket.off('disconnect', handleDisconnect);
      socket.off('connect', handleReconnect);
      socket.off('new_detection', handleNewDetection);
      socket.off('new_detection_group', handleNewDetectionGroup);
      socket.off('detection_group_updated', handleDetectionGroupUpdated);
      socket.off('new_broadband_suppression', handleNewBroadbandSuppression);
      socket.off('new_suppression', handleNewSuppression);
      socket.off('detection_group_status_changed', handleDetectionGroupStatusChanged);
      socket.off('device_updated', handleDeviceUpdated);
      socket.off('device_connected', handleDeviceConnected);
      socket.off(
        'device_active_detection_groups_counter_updated',
        handleActiveDetectionGroupsCountUpdated,
      );
      socket.off('firmware_update_status', handleFirmwareUpdateStatus);
      socket.off('jumper_session_updated', handleJumperSessionUpdated);
      releaseFrontendSocket();
    };
  }, [
    onDetectionGroupStatusChange,
    onFirmwareUpdateStatus,
    onNewDetection,
    onNewDetectionGroup,
    onNewBroadbandSuppression,
    onNewSuppression,
    queryClient,
  ]);

  // Subscribe/unsubscribe when deviceIds change
  useEffect(() => {
    if (!deviceIds || deviceIds.length === 0) return;

    let cancelled = false;

    subscribeDevices(deviceIds, (ack: SubscribeAck) => {
      if (cancelled) {
        return;
      }

      const rejectedIds = new Set(ack.rejected);
      const hasAcceptedSubscription = deviceIds.some((deviceId) => !rejectedIds.has(deviceId));

      if (hasAcceptedSubscription) {
        void refetchDevicesAndAnnounceNewAlerts(queryClient);
      }
    });

    return () => {
      cancelled = true;
      const released = unsubscribeDevices(deviceIds);
      if (released.length > 0) {
        useActiveSuppressionsStore.getState().removeDevices(released);
      }
    };
  }, [deviceIds, queryClient]);
};
